import { FC, useContext, useEffect, useSyncExternalStore } from 'react';
import { FlightFilterModel } from 'ts/flight/classes/FlightFilterModel';
import { FlightSearch } from 'ts/flight/interfaces/FlightSearch';
import { MainContext } from 'contexts/MainContext';
import { FlightsResultContext } from 'contexts/FlightsResultContext';
import {
  findFlightForCardAction,
  flightCardLabel,
  scrollFlightCardIntoView,
} from 'components/flight/Result/flightCardActions';
import {
  applyAssistantFlightFilters,
  finishSearchLoading,
  getPendingFlightCardActionsVersion,
  getPendingFlightFiltersVersion,
  hasPendingFlightCardAction,
  peekPendingFlightCardAction,
  peekPendingFlightFilters,
  publishLocalFlightCardAction,
  setPageFlightsForAssistant,
  subscribeToPendingFlightCardActions,
  subscribeToPendingFlightFilters,
  takePendingFlightCardAction,
  takePendingFlightFilters,
} from 'utils/assistantChatStore';
import { highlightAssistantTargets } from 'utils/assistantHighlight';

interface FlightAssistantBridgeProps {
  filter?: FlightFilterModel;
  setFilter: (filter: FlightFilterModel) => void;
  onSortChange?: (sort: string) => void;
  currentSortBy?: string;
  flights?: FlightSearch[];
  sessionStorageKey: string;
}

const FlightAssistantBridge: FC<FlightAssistantBridgeProps> = ({
  filter,
  setFilter,
  onSortChange,
  currentSortBy,
  flights,
  sessionStorageKey,
}) => {
  const { proposal, setProposal } = useContext(MainContext);
  const flightContext = useContext(FlightsResultContext);
  const queued = useSyncExternalStore(
    subscribeToPendingFlightFilters,
    getPendingFlightFiltersVersion,
  );
  const cardQueued = useSyncExternalStore(
    subscribeToPendingFlightCardActions,
    getPendingFlightCardActionsVersion,
  );

  const applyPending = (current: FlightFilterModel, chatKey: number) => {
    const pending = takePendingFlightFilters(chatKey);
    const result = applyAssistantFlightFilters(current, pending, {
      onChangeFilter: setFilter,
      onSortChange,
      currentSortBy,
    });
    if (result.changedKinds.length) {
      highlightAssistantTargets(result.changedKinds);
    }
  };

  useEffect(() => {
    const visible = (flights || [])
      .filter((flight) => flight.visible !== false)
      .map((flight) => ({
        id: flight.id,
        label: flightCardLabel(flight),
      }));
    setPageFlightsForAssistant(0, visible);
    setPageFlightsForAssistant(2, visible);
    return () => {
      setPageFlightsForAssistant(0, []);
      setPageFlightsForAssistant(2, []);
    };
  }, [flights]);

  useEffect(() => {
    [0, 2].forEach((chatKey) => {
      const pending = peekPendingFlightFilters(chatKey);
      if (!pending || pending.waitForFlights) {
        return;
      }
      if (!filter || !(filter.price?.max > 0)) {
        pending.waitForFlights = true;
        return;
      }
      applyPending(filter, chatKey);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queued]);

  useEffect(() => {
    if (!filter || !(filter.price?.max > 0)) {
      return;
    }
    [0, 2].forEach((chatKey) => {
      if (peekPendingFlightFilters(chatKey)) {
        applyPending(filter, chatKey);
      }
      if (finishSearchLoading(chatKey)) {
        highlightAssistantTargets(['list']);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    if (!flights?.length) {
      return;
    }

    [0, 2].forEach((chatKey) => {
      if (!hasPendingFlightCardAction(chatKey)) {
        return;
      }
      const pending = peekPendingFlightCardAction(chatKey);
      if (!pending) {
        return;
      }

      const flight = findFlightForCardAction(flights, pending);
      takePendingFlightCardAction(chatKey);
      if (!flight) {
        return;
      }

      highlightAssistantTargets(['list']);
      scrollFlightCardIntoView(flight.id);

      if (pending.action === 'select') {
        flightContext?.onClickChoose(flight, sessionStorageKey);
        return;
      }

      if (pending.action === 'add-to-proposal') {
        if (!proposal || !setProposal) {
          return;
        }
        const existingIndex = proposal.flights.findIndex((item) => item.item.id === flight.id);
        const shouldAdd =
          pending.added === true || (pending.added == null && existingIndex === -1);
        if (shouldAdd && existingIndex === -1) {
          const priceObj = flight.flighPrice.grossAndTaxWithMarkup;
          setProposal({
            ...proposal,
            flights: [
              ...proposal.flights,
              {
                item: JSON.parse(JSON.stringify(flight)),
                currency: priceObj.currency,
                price: priceObj.value,
                displayPrice: true,
                searchToken: sessionStorage.getItem(sessionStorageKey) || '',
              },
            ],
          });
        } else if (!shouldAdd && existingIndex !== -1) {
          setProposal({
            ...proposal,
            flights: proposal.flights.filter((_, index) => index !== existingIndex),
          });
        }
        return;
      }

      if (pending.action === 'info') {
        publishLocalFlightCardAction({
          kind: 'info',
          flightId: flight.id,
          show: pending.show,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardQueued, flights]);

  return null;
};

export default FlightAssistantBridge;
